import type { TarotCard } from "@/entities/tarot-card/model/types";
import { create } from "zustand";

export type AnalysisRequestStatus = "idle" | "in_progress" | "completed" | "failed";

type AnalysisRequestKeyInput = {
  spreadName: string | undefined;
  cards: Pick<TarotCard, "name" | "reversed" | "img_src">[];
  question?: string | null;
};

export function buildAnalysisRequestKey({
  spreadName,
  cards,
  question,
}: AnalysisRequestKeyInput): string {
  return JSON.stringify({
    spreadName: spreadName ?? "",
    cards: cards.map((card) => ({
      name: card.name,
      reversed: card.reversed,
      img_src: card.img_src,
    })),
    question: question ?? "",
  });
}

function isActiveStatus(status: AnalysisRequestStatus): boolean {
  return status === "in_progress" || status === "completed";
}

/**
 * Whether the analysis panel may start a request on its own.
 *
 * Only a request key that has never been tried (or after a reset) starts
 * automatically. A failed key must not restart by itself: the failure flips the
 * status and would otherwise retrigger the panel effect, producing an endless
 * POST loop.
 */
export function shouldAutoStartAnalysis(
  state: Pick<AnalysisStoreState, "requestKey" | "requestStatus">,
  currentRequestKey: string
): boolean {
  return state.requestKey !== currentRequestKey || state.requestStatus === "idle";
}

/** UUID for the server-side idempotency key. */
export function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // `randomUUID` needs a secure context; fall back to a v4 UUID by hand.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

type AnalysisStoreState = {
  analysis: string;
  isLoading: boolean;
  requestKey: string | null;
  /**
   * Idempotency key sent with every POST of the current request. It survives a
   * failed attempt of the same input, so a retry can never be charged twice,
   * and is dropped by `resetRequest`.
   */
  requestId: string | null;
  requestStatus: AnalysisRequestStatus;
};

type AnalysisStore = AnalysisStoreState & {
  setAnalysis: (analysis: string) => void;
  replaceAnalysis: (analysis: string) => void;
  clearAnalysis: () => void;
  setIsLoading: (isLoading: boolean) => void;
  beginRequest: (key: string) => boolean;
  completeRequest: (key: string) => void;
  failRequest: (key: string) => void;
  markCompleted: (key: string) => void;
  resetRequest: () => void;
};

const useAnalysisStore = create<AnalysisStore>()((set, get) => ({
  analysis: "",
  setAnalysis: (analysis) =>
    set((state) => ({ analysis: state.analysis + analysis })),
  replaceAnalysis: (analysis) => set({ analysis }),
  clearAnalysis: () => set({ analysis: "" }),
  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),
  requestKey: null,
  requestId: null,
  requestStatus: "idle",
  beginRequest: (key) => {
    const { requestKey, requestId, requestStatus } = get();
    if (requestKey === key && isActiveStatus(requestStatus)) {
      return false;
    }
    set({
      requestKey: key,
      requestStatus: "in_progress",
      requestId: requestKey === key && requestId ? requestId : createRequestId(),
    });
    return true;
  },
  completeRequest: (key) => {
    if (get().requestKey !== key) {
      return;
    }
    set({ requestStatus: "completed" });
  },
  failRequest: (key) => {
    if (get().requestKey !== key) {
      return;
    }
    set({ requestStatus: "failed" });
  },
  markCompleted: (key) =>
    set({ requestKey: key, requestId: null, requestStatus: "completed" }),
  resetRequest: () =>
    set({ requestKey: null, requestId: null, requestStatus: "idle" }),
}));

export default useAnalysisStore;
